import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import scrollToTop from "../../../utils/scrollToTop";
import useCreateUrl from "../../../hooks/useCreateUrl";
import { SEARCH_PAGE_SIZE } from "@flights/constants";
import { useI18n, t } from "@bookingcom/lingojs-react";
import { PaginationProps } from "@bookingcom/bui-react";
import { actions } from "../../../store/search/actions";
import { useLocation, useHistory } from "react-router-dom";
import { useGetAvailableFilters } from "../SearchResults/components/boundsFilterHooks";
import useProfiler from "../../../hooks/useProfiler";
import flights_merch_toolkit_offer_pinning_v2 from "../../../utils/experiments/genius/flights_merch_toolkit_offer_pinning_v2";
import flights_lve_merch_toolkit_offer_highlighting from "../../../utils/experiments/genius/flights_lve_merch_toolkit_offer_highlighting";

export default function useSearchPaginationController() {
  const i18n = useI18n();
  const dispatch = useDispatch();
  const history = useHistory();
  const { search, pathname } = useLocation();
  const { enhanceURLSearchParams } = useCreateUrl();
  const { totalCount, filteredTotalCount } = useGetAvailableFilters();

  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const totalPages = useMemo(() => {
    const total = totalCount ? Math.ceil(totalCount / SEARCH_PAGE_SIZE) : 0;
    return filteredTotalCount ? Math.ceil(filteredTotalCount / SEARCH_PAGE_SIZE) : total;
  }, [totalCount, filteredTotalCount]);

  const currentPage = useMemo(() => {
    return Number(qs.get("page") || 1);
  }, [qs]);

  /* start - flights_merch_toolkit_offer_pinning_v2 */
  const { isLoggedIn } = useProfiler();
  /* end - flights_merch_toolkit_offer_pinning_v2 */

  const onPageChange = useCallback<NonNullable<PaginationProps["onPageChange"]>>(
    ({ page }) => {
      const target = Math.max(1, Math.min(page, totalPages));
      dispatch(actions.setPage(target));
      scrollToTop();

      /* start - flights_merch_toolkit_offer_pinning_v2 */
      if (target > 1 && isLoggedIn()) {
        flights_merch_toolkit_offer_pinning_v2.goals.next_search_page_navigation();
        flights_lve_merch_toolkit_offer_highlighting.goals.next_search_page_navigation();
      }
      /* end - flights_merch_toolkit_offer_pinning_v2 */

      qs.set("page", `${target}`);
      enhanceURLSearchParams(qs);
      history.push({ pathname: pathname, search: qs.toString() }, { hideLoader: true });
    },
    [qs, history, dispatch, pathname, enhanceURLSearchParams, totalPages, isLoggedIn]
  );

  const canRender = useMemo(() => totalPages > 1, [totalPages]);

  return {
    canRender,
    totalPages,
    currentPage,
    onPageChange,
    ariaPageLabelPrefix: "",
    ariaPreviousLabel: i18n.trans(t("flights_action_back")),
    ariaNextLabel: i18n.trans(t("flights_action_next"))
  };
}
